import classNames from "classnames";
import { FC, memo } from "react";

import { ReactComponent as GalleryIcon } from "assets/images/common/gallery.svg";

import { AssetManager } from "entities/assets";

import styles from "./style.module.scss";

export type AssetCardData = {
  id: string | number;
  image?: string;
  compliance: number | null;
  activeIncidents: number;
  name: string;
  type: string | null;
  description: string | null;
  manager?: AssetManager;
};

type Tone = "green" | "orange" | "red" | "gray";

const toneFor = (compliance: number | null): Tone => {
  if (compliance === null) return "gray";
  if (compliance >= 90) return "green";
  if (compliance >= 40) return "orange";
  return "red";
};

type PropTypes = {
  data: AssetCardData;
  className?: string;
  onClick?: () => void;
};

const AssetCard: FC<PropTypes> = ({ data, className, onClick }) => {
  const tone = toneFor(data.compliance);
  const incidentWord = `active incident${data.activeIncidents === 1 ? "" : "s"}`;

  return (
    <article className={classNames(styles.card, className, { [styles.clickable]: Boolean(onClick) })} onClick={onClick}>
      <div className={styles.image}>
        {!!data.image ? (
          <img src={data.image} alt={data.name} />
        ) : (
          <div className={styles.placeholder}>
            <GalleryIcon />
          </div>
        )}
      </div>

      <div className={styles.badges}>
        <span className={classNames(styles.badge, styles[`tone-${tone}`])}>
          {data.compliance === null ? (
            <span className={styles.badgeValue}>-</span>
          ) : (
            <>
              <span className={styles.badgeValue}>{data.compliance}%</span>
              <span className={styles.badgeWord}>compliance</span>
            </>
          )}
        </span>

        <span className={classNames(styles.badge, styles[`tone-${tone}-light`])}>
          <span className={styles.badgeValue}>{data.activeIncidents}</span>
          <span className={styles.badgeWord}>{incidentWord}</span>
        </span>
      </div>

      <div className={styles.content}>
        <div className={styles.titleBlock}>
          <p className={styles.name}>{data.name}</p>
          <span className={styles.type}>{data.type}</span>
        </div>

        <p className={styles.description}>{data.description}</p>

        {data.manager && (
          <div className={styles.manager}>
            <span className={styles.avatar}>{data.manager.full_name?.charAt(0)}</span>

            <div className={styles.managerInfo}>
              <p className={styles.managerName}>{data.manager.full_name}</p>
              <p className={styles.managerRole}>{data.manager.position}</p>
            </div>
          </div>
        )}
      </div>
    </article>
  );
};

export default memo(AssetCard);
